import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Loader from '../../component/common/Loader';
import SearchInput from '../../component/common/Input/SearchInput';
import Table from '../../component/common/Table';
import Pagination from '../../component/common/table2/Pagination';
import { formatMoneyExact } from './marketplaceHubApi';
import { TableCard } from '../../component/common/designSystem';

const PER_PAGE = 10;

/**
 * Shared, searchable users table for the Marketplace section.
 * Columns are order-shaped, so this is separate from the sleep/breathwork/habit
 * tables. The company page supplies its own company/v1 `fetcher`.
 */
const MarketplaceUsersTable = ({
  onOpenUser,
  tableName,
  fetcher,
  showCompanyColumn = false,
  department = ''
}) => {
  const [loader, setLoader] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(
    async (page, search) => {
      setLoader(true);
      try {
        const response = await fetcher({ page, perPage: PER_PAGE, search, department });
        // Users who never redeemed show dashes, not zeros and £0.00.
        const mapped = (response.data || []).map((row) => ({
          ...row,
          ordersText: row.orders > 0 ? `${row.orders}` : '-',
          valueText: row.orders > 0 ? formatMoneyExact(row.totalValue) : '-',
          fulfilledText: row.orders > 0 ? `${row.successfulOrders} of ${row.orders}` : '-',
          lastOrderDate: row.lastOrderDate || '-'
        }));
        setRows(mapped);
        setTotalCount(response.totalCount || 0);
      } catch (error) {
        console.error('MarketplaceUsersTable fetchData error:', error);
        setRows([]);
        setTotalCount(0);
      } finally {
        setLoader(false);
      }
    },
    [fetcher, department]
  );

  // Debounced fetch on search / page / scope change.
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData(currentPage, searchTerm);
    }, 200);
    return () => clearTimeout(timeout);
  }, [fetchData, searchTerm, currentPage]);

  const columns = [
    { title: 'Name', key: 'user_profile', sortable: false, align: 'left', type: 'profile' },
    ...(showCompanyColumn
      ? [{ title: 'Company', key: 'company', sortable: false, align: 'center', type: 'text3' }]
      : []),
    { title: 'Orders', key: 'ordersText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Spend', key: 'valueText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Fulfilled', key: 'fulfilledText', sortable: false, align: 'center', type: 'text3' },
    {
      title: 'Last Order',
      key: 'lastOrderDate',
      sortable: false,
      align: 'center',
      type: 'text3'
    },
    ...(onOpenUser
      ? [{ title: '', key: 'action', align: 'right', type: 'action', arrowClickFun: onOpenUser }]
      : [])
  ];

  return (
    <div>
      {loader ? <Loader /> : null}

      <div className="flex justify-end">
        <SearchInput
          id={`${tableName}-search`}
          name={`${tableName}-search`}
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setCurrentPage(1);
            setSearchTerm(e.target.value);
          }}
          placeholder="Search users"
          className="w-full sm:w-auto px-3 py-2"
        />
      </div>

      <TableCard
          pagination={{
            currentPage: currentPage,
            totalCount: totalCount,
            pageSize: PER_PAGE,
            onPageChange: (page) => setCurrentPage(page)
          }}
        >
          <Table
          columns={columns}
          data={rows}
          name={tableName}
          bottomBorder={totalCount > PER_PAGE}
          loader={loader}
        />
        </TableCard>

      
    </div>
  );
};

MarketplaceUsersTable.propTypes = {
  onOpenUser: PropTypes.func,
  tableName: PropTypes.string,
  fetcher: PropTypes.func.isRequired,
  showCompanyColumn: PropTypes.bool,
  department: PropTypes.string
};

export default MarketplaceUsersTable;
